import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase';

const AuthContext = createContext(null);
const LOCAL_USERS_KEY = 'sm_kinetocut_local_users';
const LOCAL_SESSION_KEY = 'sm_kinetocut_local_session';

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!auth || !isFirebaseConfigured) {
      const session = readJson(LOCAL_SESSION_KEY, null);
      setUser(session);
      setIsReady(true);
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          id: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
        });
      } else {
        setUser(null);
      }
      setIsReady(true);
    });
    return () => unsub();
  }, []);

  const login = async (email, password) => {
    if (!auth || !isFirebaseConfigured) {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      const cleanPassword = String(password || '');
      const users = readJson(LOCAL_USERS_KEY, []);
      const matched = users.find(
        (item) =>
          String(item.email || '').toLowerCase() === normalizedEmail &&
          String(item.password || '') === cleanPassword
      );

      if (!matched) {
        return { ok: false, message: 'Invalid email or password.' };
      }

      const localUser = { id: matched.id, name: matched.name, email: matched.email };
      writeJson(LOCAL_SESSION_KEY, localUser);
      setUser(localUser);
      return { ok: true };
    }

    try {
      await signInWithEmailAndPassword(auth, String(email || '').trim(), String(password || ''));
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/user-not-found' ||
        code === 'auth/wrong-password'
      ) {
        return { ok: false, message: 'Invalid email or password.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      return { ok: false, message: 'Unable to login right now. Please try again.' };
    }
  };

  const register = async ({ name, email, password }) => {
    const cleanName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const cleanPassword = String(password || '');

    if (!cleanName || !normalizedEmail || !cleanPassword) {
      return { ok: false, message: 'All fields are required.' };
    }
    if (cleanPassword.length < 6) {
      return { ok: false, message: 'Password must be at least 6 characters.' };
    }

    if (!auth || !isFirebaseConfigured) {
      const users = readJson(LOCAL_USERS_KEY, []);
      const emailInUse = users.some(
        (item) => String(item.email || '').toLowerCase() === normalizedEmail.toLowerCase()
      );
      if (emailInUse) {
        return { ok: false, message: 'An account with this email already exists.' };
      }

      const localUser = {
        id: `local-${Date.now()}`,
        name: cleanName,
        email: normalizedEmail,
      };
      const localUserWithPassword = { ...localUser, password: cleanPassword };
      writeJson(LOCAL_USERS_KEY, [...users, localUserWithPassword]);
      writeJson(LOCAL_SESSION_KEY, localUser);
      setUser(localUser);
      return { ok: true };
    }

    try {
      const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, cleanPassword);
      await updateProfile(credential.user, { displayName: cleanName });
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/email-already-in-use') {
        return { ok: false, message: 'An account with this email already exists.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      if (code === 'auth/weak-password') {
        return { ok: false, message: 'Password is too weak. Use at least 6 characters.' };
      }
      return { ok: false, message: 'Unable to register right now. Please try again.' };
    }
  };

  const logout = async () => {
    if (!auth || !isFirebaseConfigured) {
      window.localStorage.removeItem(LOCAL_SESSION_KEY);
      setUser(null);
      return;
    }
    await signOut(auth);
  };

  const loginWithGoogle = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Google login requires Firebase configuration.' };
    }
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/popup-closed-by-user') {
        return { ok: false, message: 'Google login was cancelled.' };
      }
      return { ok: false, message: 'Unable to login with Google right now.' };
    }
  };

  const loginWithApple = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Apple login requires Firebase configuration.' };
    }
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      await signInWithPopup(auth, provider);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/popup-closed-by-user') {
        return { ok: false, message: 'Apple login was cancelled.' };
      }
      return { ok: false, message: 'Unable to login with Apple right now.' };
    }
  };

  const updateUserProfile = async ({ name }) => {
    const cleanName = String(name || '').trim();
    if (!cleanName) {
      return { ok: false, message: 'Name is required.' };
    }

    if (!auth || !isFirebaseConfigured) {
      const session = readJson(LOCAL_SESSION_KEY, null);
      if (!session) {
        return { ok: false, message: 'You must be signed in to update your profile.' };
      }
      const patchedSession = { ...session, name: cleanName };
      writeJson(LOCAL_SESSION_KEY, patchedSession);

      const users = readJson(LOCAL_USERS_KEY, []);
      const patchedUsers = users.map((item) =>
        item.id === session.id ? { ...item, name: cleanName } : item
      );
      writeJson(LOCAL_USERS_KEY, patchedUsers);

      setUser(patchedSession);
      return { ok: true };
    }

    try {
      await updateProfile(auth.currentUser, { displayName: cleanName });
      setUser((current) => (current ? { ...current, name: cleanName } : current));
      return { ok: true };
    } catch (error) {
      return { ok: false, message: 'Unable to update profile right now. Please try again.' };
    }
  };

  const value = {
    user,
    isReady,
    isAuthenticated: Boolean(user),
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithApple,
    updateUserProfile,
    isFirebaseConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
